import { DecimalPipe } from '@angular/common';
import {
  Component,
  inject,
  OnInit,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NgxPermissionsService } from 'ngx-permissions';
import { MessageService, PrimeIcons } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { lastValueFrom, map } from 'rxjs';
import {
  ProductVariantSummary,
  ShippingOrderControllerService,
  ShippingOrderItem,
  ShippingOrderRequestItem,
  ShippingOrderRequestItemPendingStock
} from 'src/app/admin-api';
import { ProductVariantSelectModalComponent } from 'src/app/components/product-select-modal/product-variant-select-modal/product-variant-select-modal.component';
import {
  TableActionButton,
  TableColumn,
  TableComponent
} from 'src/app/components/table';
import { Role, roleAsObject } from 'src/app/models';
import { ShippingOrderItemUpdateModalComponent } from 'src/app/pages/products/shipping-orders/shipping-order-detail/shipping-order-item-update-modal/shipping-order-item-update-modal.component';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';

@Component({
  selector: 'app-waiting-stock',
  templateUrl: './waiting-stock.component.html',
  styleUrl: './waiting-stock.component.scss',
  encapsulation: ViewEncapsulation.None,
  providers: [DialogService, MessageService, DecimalPipe]
})
export class WaitingStockComponent implements OnInit {
  public shippingOrderService = inject(ShippingOrderControllerService);
  private title = inject(Title);
  private dialogService = inject(DialogService);
  private messageService = inject(MessageService);
  private ngxPermissionService = inject(NgxPermissionsService);
  private decimalPipe = inject(DecimalPipe);

  @ViewChild(TableComponent) table: TableComponent;

  productCols = [
    new TableColumn.Builder()
      .setHeader('')
      .setField(' ')
      .setType('checkbox')
      .build(),
    new TableColumn.Builder()
      .setHeader('Pedido')
      .setField('shippingOrderId')
      .setRouterLink('/products/shipping-orders/')
      .setRouterLinkFieldName('shippingOrderId')
      .build(),
    new TableColumn.Builder()
      .setHeader('Id do Ticket')
      .setField('ticketId')
      .setType('number')
      .setRouterLink('https://b4asupport.zendesk.com/agent/tickets/')
      .setRouterLinkFieldName('ticketId')
      .build(),
    new TableColumn.Builder()
      .setHeader('Motivo')
      .setField('ticketDetail')
      .setCondition('contains')
      .build(),
    new TableColumn.Builder()
      .setHeader('Data pedido')
      .setField('dateCreated')
      .setType('date')
      .setCondition('between')
      .build(),
    new TableColumn.Builder()
      .setHeader('Email')
      .setField('email')
      .setRouterLink('/users/person/')
      .setRouterLinkFieldName('personId')
      .build(),
    new TableColumn.Builder().setHeader('GlamId').setField('glamId').build(),
    new TableColumn.Builder()
      .setHeader('Imagem')
      .setField('defaultImageURL')
      .setType('image')
      .setFilter(false)
      .setStyleClass('product-image')
      .build(),
    new TableColumn.Builder()
      .setHeader('Produto')
      .setField('productVariantName')
      .setRouterLink('/products/catalog/product-variant/')
      .setRouterLinkFieldName('productVariantId')
      .setCondition('contains')
      .build(),
    new TableColumn.Builder()
      .setHeader('EAN')
      .setField('internalEAN')
      .setRouterLink('/products/catalog/product-variant/')
      .setRouterLinkFieldName('productVariantId')
      .setType('number')
      .build(),
    new TableColumn.Builder()
      .setHeader('Ação')
      .setField(' ')
      .setType('button')
      .build(),
    new TableColumn.Builder()
      .setHeader('SKU Admin')
      .setField('productVariantId')
      .setRouterLink('/products/catalog/product-variant/')
      .setRouterLinkFieldName('productVariantId')
      .setType('number')
      .build(),
    new TableColumn.Builder()
      .setHeader('SKU Millennium')
      .setField('externalId')
      .setRouterLink('/products/catalog/product-variant/')
      .setRouterLinkFieldName('productVariantId')
      .setType('number')
      .build(),
    new TableColumn.Builder()
      .setHeader('Categoria')
      .setField('category')
      .setCondition('contains')
      .build(),
    new TableColumn.Builder()
      .setHeader('Estoque')
      .setField('inStockAcquisition')
      .setType('formattedInteger')
      .build()
  ];
  substitutionCols = [
    new TableColumn.Builder()
      .setHeader('Imagem')
      .setField('defaultImageURL')
      .setType('image')
      .setFilter(false)
      .setStyleClass('product-image')
      .setDisplayFunction(
        (item: ShippingOrderRequestItemPendingStock) =>
          item.defaultImageURL || 'assets/images/no_img_available.jpg'
      )
      .build(),
    new TableColumn.Builder()
      .setHeader('Produto')
      .setField('productVariantName')
      .setRouterLink('/products/catalog/product-variant/')
      .setRouterLinkFieldName('productVariantId')
      .setCondition('contains')
      .build(),
    new TableColumn.Builder()
      .setHeader('EAN')
      .setField('internalEAN')
      .setRouterLink('/products/catalog/product-variant/')
      .setRouterLinkFieldName('productVariantId')
      .setType('number')
      .build(),
    new TableColumn.Builder()
      .setHeader('Pedidos')
      .setField('orders')
      .setType('number')
      .setCondition('gte')
      .build()
  ];

  reviewModalVisible = false;
  permission = false;
  products: ShippingOrderRequestItemPendingStock[];
  productsSelection: ShippingOrderRequestItemPendingStock[];
  productsGrouped: Array<
    ShippingOrderRequestItemPendingStock & { orders: number }
  >;
  product: ProductVariantSummary;
  showTable = false;

  productSelectionModal = (
    item?:
      | ShippingOrderRequestItemPendingStock[]
      | ShippingOrderRequestItemPendingStock
  ) => {
    const header = Array.isArray(item)
      ? `Substituir ${this.decimalPipe.transform(item.length || 0)} produtos`
      : `Substituir produto ${item.productVariantName} (EAN: ${item.internalEAN})`;
    this.dialogService
      .open(ProductVariantSelectModalComponent, {
        header,
        dismissableMask: undefined,
        width: '60%',
        data: {
          type: 1,
          sorting: 'stock',
          buttonLabel: 'Avançar',
          sortingEnable: false,
          minStock: Array.isArray(item) ? item.length : 1,
          search: Array.isArray(item)
            ? undefined
            : item?.productVariantName.split(' ')?.at(0)
        }
      })
      .onClose.subscribe(async (product: ProductVariantSummary) => {
        if (product) {
          if (Array.isArray(item)) {
            this.reviewModalVisible = true;
            this.productsSelection = [...item];
            this.product = { ...product };
            this.productsGrouped = this.productsSelection?.reduce(
              (acc, product) => {
                if (
                  acc.some(
                    (p) => p.productVariantId === product.productVariantId
                  )
                ) {
                  acc.find(
                    (p) => p.productVariantId === product.productVariantId
                  ).orders!++;
                } else {
                  acc.push({ ...product, orders: 1 });
                }
                return acc;
              },
              [] as Array<
                ShippingOrderRequestItemPendingStock & { orders: number }
              >
            );
          } else {
            LoaderService.showLoader();
            const shippingOrder = await lastValueFrom(
              this.shippingOrderService
                .getShippingOrderById(item.shippingOrderId)
                .pipe(map((data) => data.result))
            );
            this.dialogService
              .open(ShippingOrderItemUpdateModalComponent, {
                header: 'Substituição de item',
                data: {
                  product,
                  shippingOrder: shippingOrder,
                  shippingOrderItem: item
                }
              })
              .onClose.subscribe(async (items: ShippingOrderItem[]) => {
                if (items?.length) {
                  this.showTable = false;
                  setTimeout(() => {
                    this.products = this.products.filter(
                      (p) =>
                        p.shippingOrderItemId !==
                        (items as ShippingOrderRequestItemPendingStock)
                          .shippingOrderItemId
                    );
                    this.showTable = true;
                  });
                  this.messageService.add({
                    severity: 'success',
                    summary: 'Sucesso',
                    detail: 'Produto substituído com sucesso.'
                  });
                } else if (items === null) this.productSelectionModal(item);
                LoaderService.showLoader(false);
              });
          }
        }
      });
  };
  actionButtons: TableActionButton[] = [
    new TableActionButton.Builder()
      .setAction(this.productSelectionModal)
      .setIcon(PrimeIcons.ARROW_RIGHT_ARROW_LEFT)
      .setSize('small')
      .setText(true)
      .setRounded(true)
      .setTooltip('Substituir produto')
      .setTooltipPosition('bottom')
      .setDisplay((item: ShippingOrderRequestItem) => {
        return this.permission && item.inStockAcquisition <= 0;
      })
      .build()
  ];

  async ngOnInit() {
    this.title.setTitle('Reenvios aguardando estoque');
    try {
      LoaderService.showLoader();
      this.permission = await this.ngxPermissionService.hasPermission([
        roleAsObject(Role.Full_Administrator).enumValue,
        roleAsObject(Role.Delivery).enumValue,
        roleAsObject(Role.Logistics).enumValue,
        roleAsObject(Role.User_Allocation).enumValue,
        roleAsObject(Role.Admin_Allocation).enumValue
      ]);
      if (!this.permission)
        this.productCols = this.productCols.filter(
          (col) => col.type === 'checkbox' || col.type === 'button'
        );
      await this.findProducts();
      this.showTable = true;
    } catch (error) {
      this.permission = false;
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async findProducts() {
    try {
      this.products = await lastValueFrom(
        this.shippingOrderService
          .findRequestsPendingStock()
          .pipe(map((data) => data.result))
      );
    } catch (error) {
      this.products = [];
      AppDialogService.showErrorDialog(error);
    }
  }

  modalClose() {
    this.reviewModalVisible = false;
    delete this.productsSelection;
    delete this.product;
  }

  async updateShippingOrderItems() {
    if (this.productsSelection?.length && this.product)
      try {
        LoaderService.showLoader();
        const result = await lastValueFrom(
          this.shippingOrderService
            .updateShippingOrderItemsResend(
              this.productsSelection.map((i) => ({
                productVariantId: this.product.productVariantId,
                shippingOrderId: i.shippingOrderId,
                shippingOrderItemId: i.shippingOrderItemId
              }))
            )
            .pipe(map((data) => data.result))
        );
        if (result?.success?.length)
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: `${result.success.length} produto(s) substituído(s) com sucesso.`
          });
        if (result?.error?.length)
          this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail: `${result.error.length} produto(s) não substituído(s).`
          });
        this.showTable = false;
        setTimeout(() => {
          this.modalClose();
          this.products = this.products.filter((item) =>
            result.success.every(
              (s) => s.shippingOrderItemId !== item.shippingOrderItemId
            )
          );
          this.showTable = true;
        });
      } catch (error) {
        AppDialogService.showErrorDialog(error);
      } finally {
        LoaderService.showLoader(false);
      }
  }

  chooseOtherProduct() {
    this.reviewModalVisible = false;
    delete this.product;
    this.productSelectionModal(this.productsSelection);
  }

  async recalculateStocks(): Promise<void> {
    try {
      LoaderService.showLoader();
      const result = await lastValueFrom(
        this.shippingOrderService
          .refreshShippingOrderRequestStocks()
          .pipe(map((data) => data.result))
      );
      if (result?.length)
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: `${result?.length} pedidos com estoque disponível.`
        });
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  get selectionSize() {
    return this.table?.selection?.length || 0;
  }
}
